import type { PropsWithChildren, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import Pills from '@rio-cloud/rio-uikit/Pills';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import useClickOutside from '@rio-cloud/rio-uikit/useClickOutside';
import { isMobile, isMobileScreen } from '@rio-cloud/rio-uikit/DeviceUtils';

import { useUikitContext } from '../UikitContext';
import { pages } from '../header/Navigation';

const SIDEBAR_WIDTH = 270;

type UikitMenuSidebarProps = {
    title: string | ReactNode;
};

const UikitMenuSidebarMainNavigation = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    return (
        <nav aria-label='Main navigation' className='position-sticky top-0 z-index-5 bg-white margin-bottom-15'>
            <Pills variant='filled' justified iconOnly>
                {pages.map(page => {
                    const isActive = pathname.startsWith(`/${page.id}`);
                    const route = `/${page.id}`;

                    return (
                        <Pills.Item
                            key={page.id}
                            active={isActive}
                            aria-current={isActive ? 'page' : undefined}
                            iconName={page.icon}
                            tooltip={page.name}
                            role='link'
                            tabIndex={0}
                            onClick={() => navigate(route)}
                            onKeyDown={event => event.key === 'Enter' && navigate(route)}
                        />
                    );
                })}
            </Pills>
        </nav>
    );
};

export const UikitMenuSidebar = (props: PropsWithChildren<UikitMenuSidebarProps>) => {
    const { title, children } = props;
    const { isApplicationHeaderMobile, isMenuFlyMode, onMenuOutsideClicked, showMenu } = useUikitContext();
    const isMobileOrSmallScreen = isMobile() || isMobileScreen();
    const showMainNavigation = !isMobile() && isApplicationHeaderMobile;
    const ref = useClickOutside(event => isMobileOrSmallScreen && onMenuOutsideClicked(event));

    return (
        <ApplicationLayout.Sidebar className='uikit-frame-menu'>
            <Sidebar
                key={isMenuFlyMode ? 'fly' : 'fluid'}
                headerClassName={showMainNavigation ? 'hidden' : ''}
                title={title}
                titleClassName='padding-left-10'
                width={SIDEBAR_WIDTH}
                disableClose
                closed={!showMenu}
                fly={isMenuFlyMode}
                hasSmoothScrollbar
                position={Sidebar.LEFT}
            >
                <div className='display-flex flex-column'>
                    {showMainNavigation && <UikitMenuSidebarMainNavigation />}
                    <div className='flex-1-1' ref={ref}>
                        {children}
                    </div>
                </div>
            </Sidebar>
        </ApplicationLayout.Sidebar>
    );
};
